import fetch from '../utils/fetch';
import { cks } from '@/utils/tool';
import { omitNilFields } from '@/utils/payment';
import {
    getClientNextActionField,
    handleClientNextAction
} from '@/services/clientNextAction';

const inflightPaymentRequests = new Map();

function withInflightDedup(key, requestFactory) {
    if (inflightPaymentRequests.has(key)) {
        return inflightPaymentRequests.get(key);
    }

    const request = Promise.resolve()
        .then(requestFactory)
        .finally(() => {
            inflightPaymentRequests.delete(key);
        });

    inflightPaymentRequests.set(key, request);
    return request;
}

// 获取支付网关信息
export function getPaymentConfig(params) {
    return fetch({
        url: '/app/account/getPaymentConfig',
        method: 'get',
        params: params
    })
}

// 获取用户钱包信息（ card & blance ）
export function getUserWallet(params) {
    return fetch({
        url: '/app/account/getCardList',
        method: 'get',
        params: params
    })
}

// 获取计费规则
export function getBillingRule(params) {
    return fetch({
        url: '/app/rent/queryCabinetByCode',
        method: 'get',
        params: params
    })
}

// 获取附近柜机列表
export function getNearCabinetList(params) {
    const normalizedParams = Object.keys(params || {}).reduce((result, key) => {
        const value = params[key]
        if (value === undefined || value === null || value === '') {
            return result
        }

        if (['lat', 'lng', 'online_status', 'distance_limit'].includes(key)) {
            result[key] = Number(value)
            return result
        }

        result[key] = value
        return result
    }, {})

    return fetch({
        url: '/app/rent/getNearCabinetList',
        method: 'get',
        params: normalizedParams
    })
}

export function getUserInfo(params) {
    return fetch({
        url: 'app/member/getMemberInfo',
        method: 'get',
        params: params
    })
}

// 创建支付订单
export function createPaymentOrder(data) {
    return fetch({
        url: '/app/rent/createRentPaymentToSavePaymentMethod',
        method: 'post',
        data: data
    })
}

// 支付完成
export function completePaymentOrder(data) {
    const payload = omitNilFields(data)
    const requestKey = `completePaymentOrder:${JSON.stringify(payload)}`

    return withInflightDedup(requestKey, () => fetch({
        url: '/app/rent/savePaymentMethodAndUpdateRentPayment',
        method: 'post',
        data: payload
    }))
}

export function doBalancePayment(data) {
    return fetch({
        url: '/app/account/doBalancePayment',
        method: 'post',
        data: data
    })
}

export function confirmBalancePayment(data) {
    return fetch({
        url: '/app/account/confirmBalancePayment',
        method: 'post',
        data: data
    })
}

// 二次支付完成(create + complete)
export function doRepayRentPayment(data) {
    return fetch({
        url: '/app/rent/doRepayRentPayment',
        method: 'post',
        data: data
    })
}

// 二次支付确认
export function confirmRepayRentPayment(data) {
    const payload = omitNilFields(data)
    const requestKey = `confirmRepayRentPayment:${JSON.stringify(payload)}`

    return withInflightDedup(requestKey, () => fetch({
        url: '/app/rent/confirmRepayRentPayment',
        method: 'post',
        data: payload
    }))
}

// 修改用户信息
export function editProfile(params) {
    return fetch({
        url: 'app/member/editProfile',
        method: 'get',
        params: params
    })
}

export function deleteAccount(params) {
    return fetch({
        url: 'app/member/deleteAccount',
        method: 'post',
        params: params
    })
}

export function reportProblem(data) {
    const token = cks.get('token')
    if (data && typeof data.append === 'function') {
        if (!data.has || !data.has('token')) {
            data.append('token', token || '')
        }
        if (!data.has || !data.has('app_version')) {
            data.append('app_version', 'w_1.0')
        }
    }

    return fetch({
        url: '/app/report/reportProblem',
        method: 'post',
        data,
        headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    })
}

// 获取Stripe绑卡clientSecret
export function getStripeSetupClientSecret(params) {
    return fetch({
        url: 'app/account/getStripeSetupClientSecret',
        method: 'get',
        params: params
    })
}

// 创建支付交易订单 / 创建绑卡请求
export function createRentOrder(params) {
    return fetch({
        url: '/app/rent/createRentOrder',
        method: 'post',
        params: params
    })
}

// 绑定卡片
export function bindPaymentMethod(params) {
    return fetch({
        url: 'app/account/bindCreditCardByStripe',
        method: 'get',
        params: params
    })
}

// 获取Stripe充值clientSecret
export function getStripePaymentIntentClientSecret(params) {
    return fetch({
        url: 'app/account/getStripePaymentIntentClientSecret',
        method: 'get',
        params: params
    })
}

// 租借
export async function borrow(data) {
    const res = await fetch({
        url: 'app/rent/borrow',
        method: 'post',
        data: data
    })

    const result = await handleClientNextAction(res, {
        beforeRedirect: async () => {
            try {
                const store = (await import('@/store')).default
                if (store && store.dispatch) {
                    store.dispatch('redirect/setRedirect', {
                        flow: 'borrow',
                        context: {
                            internal_id: getClientNextActionField(res, 'internal_id'),
                            rent_payment_id: res && res.data && res.data.rent_payment_id,
                            subscription_id: data && data.subscription_id,
                        }
                    })
                }
            } catch (_) {}
        }
    })

    if (result.handled) {
        return new Promise(() => {})
    }

    return res
}

// 查询是否有租借中订单
export function queryBorrowStatus(params) {
    return fetch({
        url: 'app/rent/queryBorrowStatus',
        method: 'get',
        params: params
    })
}

// 查询是否有未支付订单
export function getUnpaidRent(params) {
    return fetch({
        url: 'app/rent/getUnpaidRent',
        method: 'get',
        params: params
    })
}


// 获取当前机器的订阅列表
export function getSubscriptions(params) {
    return fetch({
        url: '/app/subscription/list',
        method: 'get',
        params: params
    })
}

// 订阅支付完成(create + complete)
export function doSubscriptionPayment(data) {
    return fetch({
        url: '/app/subscription/doSubscriptionPayment/V2',
        method: 'post',
        data: data
    })
}

// 确认第三方支付的支付意图
export function confirmPaymentIntent(data) {
    return fetch({
        url: '/app/account/confirmPaymentIntent',
        method: 'post',
        data: data
    })
}

// 删除已保存的卡
export function deleteCard(data) {
    return fetch({
        url: '/app/account/deleteCard',
        method: 'post',
        data: data
    })
}

// 订阅支付确认
export function confirmSubscriptionPayment(data) {
    return fetch({
        url: '/app/subscription/confirmSubscriptionPaymentAndBindCard/V2',
        method: 'post',
        data: data
    })
}
// 获取商户信息
export function getGatewayMerchantDetail(params) {
    return fetch({
        url: '/app/account/fortis/GetMerchantDetail',
        method: 'get',
        params: params
    })
}

// Apple商户验证
export function applePayValidateMerchant(params) {
    return fetch({
        url: '/app/account/fortis/ApplePayValidateMerchant',
        method: 'get',
        params: params
    })
}

// oneclick 绑卡请求
export function doBindCardPayment(data) {
    return fetch({
        url: '/app/account/doBindCardPayment',
        method: 'post',
        data: data
    })
}

// oneclick 绑卡确认
export function confirmBindCardPayment(data) {
    return fetch({
        url: '/app/account/confirmBindCardPayment',
        method: 'post',
        data: data
    })
}
